/* global axios */
import ApiClient from '../ApiClient';

class AiFocusAgentAPI extends ApiClient {
  constructor() {
    super('aifocus/agents', { accountScoped: true });
  }

  get({ page = 1, searchKey } = {}) {
    return axios.get(this.url, {
      params: {
        page,
        search_key: searchKey,
      },
    });
  }

  show(id) {
    return axios.get(`${this.url}/${id}`);
  }

  create(data) {
    return axios.post(this.url, { agent: data });
  }

  update(id, data) {
    const numId = Number(id);
    if (!Number.isInteger(numId) || numId <= 0) {
      return Promise.reject(new Error('Invalid agent ID'));
    }
    return axios.put(`${this.url}/${numId}`, { agent: data });
  }

  delete(id) {
    const numId = Number(id);
    if (!Number.isInteger(numId) || numId <= 0) {
      return Promise.reject(new Error('Invalid agent ID'));
    }
    return axios.delete(`${this.url}/${numId}`);
  }

  sync(id) {
    const numId = Number(id);
    if (!Number.isInteger(numId) || numId <= 0) {
      return Promise.reject(new Error('Invalid agent ID'));
    }
    return axios.post(`${this.url}/${numId}/sync`);
  }

  syncAll() {
    return axios.post(`${this.url}/sync_all`);
  }

  playground({ agentId, messageContent, messageHistory, sessionId, files }) {
    return axios.post(`${this.url}/${agentId}/playground`, {
      message_content: messageContent,
      message_history: messageHistory,
      session_id: sessionId,
      files: files,
    });
  }

  // Tools endpoints
  getTools() {
    return axios.get(`${this.url}/tools`);
  }

  getAgentTools(agentId) {
    return axios.get(`${this.url}/${agentId}/tools`);
  }

  updateAgentTools(agentId, tools) {
    return axios.put(`${this.url}/${agentId}/tools`, { tools });
  }

  // MCP Server endpoints
  getMcpServers() {
    return axios.get(`${this.url}/mcp_servers`);
  }

  getMcpTemplates() {
    return axios.get(`${this.url}/mcp_templates`);
  }

  getAgentMcpServers(agentId) {
    return axios.get(`${this.url}/${agentId}/mcp_servers`);
  }

  updateAgentMcpServers(agentId, mcpServers) {
    return axios.put(`${this.url}/${agentId}/mcp_servers`, {
      mcp_servers: mcpServers,
    });
  }

  testMcpServer(serverConfig) {
    return axios.post(`${this.url}/mcp_servers/test`, { server: serverConfig });
  }

  // Guardrails endpoints
  getGuardrailTypes() {
    return axios.get(`${this.url}/guardrails/types`);
  }

  getAgentGuardrails(agentId) {
    return axios.get(`${this.url}/${agentId}/guardrails`);
  }

  updateAgentGuardrails(agentId, guardrails) {
    return axios.put(`${this.url}/${agentId}/guardrails`, { guardrails });
  }

  testGuardrails(agentId, message) {
    return axios.post(`${this.url}/${agentId}/guardrails/test`, { message });
  }

  // Model and Provider endpoints
  getModels() {
    return axios.get(`${this.url}/models`);
  }

  getApiKeys() {
    return axios.get(`${this.url}/api_keys`);
  }

  getAgentTypes() {
    return axios.get(`${this.url}/agent_types`);
  }

  getProviders() {
    return axios.get(`${this.url}/providers`);
  }

  getCapabilities() {
    return axios.get(`${this.url}/capabilities`);
  }

  // Custom tools HTTP endpoints
  testHttpTool(toolConfig) {
    return axios.post(`${this.url}/tools/test_http`, { tool: toolConfig });
  }

  // Chatwoot Tools Templates
  getChatwootTools() {
    return axios.get(`${this.url}/chatwoot_tools`);
  }

  // Streaming endpoint for real-time responses
  streamChat({ agentId, sessionId, message }) {
    return `${this.url.replace('/api/v1', '')}/stream/${agentId}/${sessionId}?message=${encodeURIComponent(message)}`;
  }

  // Memory endpoints
  getMemory(sessionId) {
    return axios.get(
      `${this.url.replace('aifocus/agents', 'memory')}/${sessionId}`
    );
  }

  clearMemory(sessionId) {
    return axios.delete(
      `${this.url.replace('aifocus/agents', 'memory')}/${sessionId}`
    );
  }
}

export default new AiFocusAgentAPI();
